import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { FileText, Download } from 'lucide-react';

type Kaart = {
  title: string;
  version: string; // YYYY-MM
  url: string;
  filename: string;
};

const FOLDER = '/vergelijkingskaarten';

// Parseer "CV - Vergelijkingskaart <titel> versie YYYY-MM.pdf"
const parseFilename = (filename: string): Kaart | null => {
  const match = filename.match(/Vergelijkingskaart\s+(.+?)\s+versie\s+(\d{4}-\d{2})\.pdf$/i);
  if (!match) return null;
  return {
    title: match[1].trim(),
    version: match[2],
    url: `${FOLDER}/${encodeURIComponent(filename)}`,
    filename,
  };
};

const collectLatest = (filenames: string[]): Kaart[] => {
  const byTitle = new Map<string, Kaart>();
  for (const filename of filenames) {
    const kaart = parseFilename(filename);
    if (!kaart) continue;
    const existing = byTitle.get(kaart.title.toLowerCase());
    if (!existing || kaart.version > existing.version) {
      byTitle.set(kaart.title.toLowerCase(), kaart);
    }
  }
  return Array.from(byTitle.values()).sort((a, b) => a.title.localeCompare(b.title, 'nl'));
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const VergelijkingskaartenDialog = ({ open, onOpenChange }: Props) => {
  const [kaarten, setKaarten] = useState<Kaart[]>([]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    fetch('/api/vergelijkingskaarten.php', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : []))
      .then((files: unknown) => {
        if (cancelled || !Array.isArray(files)) return;
        setKaarten(collectLatest(files.filter((f): f is string => typeof f === 'string')));
      })
      .catch(() => {
        if (!cancelled) setKaarten([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Vergelijkingskaarten</DialogTitle>
          <DialogDescription className="text-left pt-2 space-y-2">
            <span className="block">
              Sinds 2024 zijn financieel adviseurs op grond van de AFM verplicht om
              een vergelijkingskaart te tonen bij bepaalde adviesproducten. Een
              vergelijkingskaart geeft u in één oogopslag inzicht in de inhoud van
              het advies, de manier waarop wij werken en de kosten die daarbij horen.
            </span>
            <span className="block">
              Zo kunt u onze dienstverlening eenvoudig vergelijken met die van
              andere adviseurs en weet u vooraf precies wat u van ons mag verwachten.
              Hieronder vindt u onze actuele vergelijkingskaarten.
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-2">
          {kaarten.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">
              Er zijn momenteel geen vergelijkingskaarten beschikbaar.
            </p>
          ) : (
            kaarten.map((kaart) => (
              <a
                key={kaart.filename}
                href={kaart.url}
                target="_blank"
                rel="noopener noreferrer"
                download={kaart.filename}
                className="flex items-center justify-between gap-4 p-4 rounded-lg border border-border hover:border-primary hover:bg-muted/50 transition-colors group focus:ring-2 focus:ring-primary focus:ring-offset-2"
              >
                <div className="flex items-start gap-3 min-w-0">
                  <FileText className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="font-medium text-foreground truncate">{kaart.title}</p>
                    <p className="text-xs text-muted-foreground">Versie {kaart.version}</p>
                  </div>
                </div>
                <Download className="w-4 h-4 text-muted-foreground group-hover:text-primary flex-shrink-0" />
              </a>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VergelijkingskaartenDialog;